import React, { useState, useEffect, useRef } from "react";
import { getCollectiveMetrics } from "../../../../Rest_api/RestApi";
import { ExchangeMetrics } from "../../../../models/metrics";
import { IndianRupee, TrendingUp, Package, Plus } from "lucide-react";
import { DynamicDatePicker } from "../../../../components/DatePicker";

// Define your primary and secondary colors.
export const PrimaryColor = "#ff7c04";
export const SecondaryColor = "#34656D";

// Interface for a filter row
interface FilterRow {
  id: string;
  startDate: string;
  endDate: string;
  exchangeName: string;
  productName: string;
  startTime: string;
  endTime: string;
}

// LocalStorage key for saving form state
const FORM_STORAGE_KEY = "exchangeMetricsFormState";

// Helper function to load form state from localStorage
const loadFormState = (): { filterRows: FilterRow[]; aggregated: boolean } | null => {
  try {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate the structure
      if (parsed.filterRows && Array.isArray(parsed.filterRows) && parsed.filterRows.length > 0) {
        return {
          filterRows: parsed.filterRows,
          aggregated: typeof parsed.aggregated === "boolean" ? parsed.aggregated : true,
        };
      }
    }
  } catch (error) {
    console.error("Error loading form state from localStorage:", error);
  }
  return null;
};

// Helper function to save form state to localStorage
const saveFormState = (filterRows: FilterRow[], aggregated: boolean) => {
  try {
    localStorage.setItem(
      FORM_STORAGE_KEY,
      JSON.stringify({ filterRows, aggregated })
    );
  } catch (error) {
    console.error("Error saving form state to localStorage:", error);
  }
};

const ExchangeMetricsForm: React.FC = () => {
  // Load saved form state from localStorage
  const savedState = loadFormState();
  
  // Form state - multiple rows (initialize with saved state or defaults)
  const [filterRows, setFilterRows] = useState<FilterRow[]>(
    savedState?.filterRows || [
      {
        id: Date.now().toString(),
        startDate: "",
        endDate: "",
        exchangeName: "IEX",
        productName: "DAM",
        startTime: "",
        endTime: "",
      },
    ]
  );
  const [aggregated, setAggregated] = useState(savedState?.aggregated ?? true);

  // API result state.
  const [metrics, setMetrics] = useState<ExchangeMetrics | ExchangeMetrics[] | null>(null);
  const [resultsAggregated, setResultsAggregated] = useState<boolean>(true); // Store the aggregated value used for current results
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isInitialMount = useRef(true);
  const isInitialAggregatedMount = useRef(true);

  // Convert time (HH:MM) to block number (1-96, where 1 = 00:00-00:15, 96 = 23:45-24:00)
  const timeToBlock = (time: string): number | null => {
    if (!time) return null;
    // Handle 24:00 specially - it maps to block 96 (23:45-24:00)
    if (time === "24:00") {
      return 96;
    }
    const [hours, minutes] = time.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    // Each day has 96 blocks of 15 minutes (24 hours * 4 blocks per hour)
    // Block 1 = 00:00-00:15, Block 2 = 00:15-00:30, ..., Block 96 = 23:45-24:00
    return hours * 4 + Math.floor(minutes / 15) + 1;
  };

  // Generate time options in 15-minute intervals
  const generateTimeOptions = (): string[] => {
    const options: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        options.push(timeStr);
      }
    }
    // Add 24:00 as the last option
    options.push("24:00");
    return options;
  };

  // Add a new filter row with values from the source row
  const addFilterRow = (sourceRowId: string) => {
    const sourceRow = filterRows.find((row) => row.id === sourceRowId);
    if (sourceRow) {
      const updatedRows = [
        ...filterRows,
        {
          id: Date.now().toString(),
          startDate: sourceRow.startDate,
          endDate: sourceRow.endDate,
          exchangeName: sourceRow.exchangeName,
          productName: sourceRow.productName,
          startTime: sourceRow.startTime,
          endTime: sourceRow.endTime,
        },
      ];
      setFilterRows(updatedRows);
      // Save to localStorage
      saveFormState(updatedRows, aggregated);
    }
  };

  // Remove a filter row
  const removeFilterRow = (id: string) => {
    if (filterRows.length > 1) {
      const updatedRows = filterRows.filter((row) => row.id !== id);
      setFilterRows(updatedRows);
      // Save to localStorage
      saveFormState(updatedRows, aggregated);
    }
  };

  // Update a specific field in a row
  const updateFilterRow = (
    id: string,
    field: keyof FilterRow,
    value: string
  ) => {
    const updatedRows = filterRows.map((row) => (row.id === id ? { ...row, [field]: value } : row));
    setFilterRows(updatedRows);
    // Save to localStorage whenever form values change
    saveFormState(updatedRows, aggregated);
  };

  // Update both start and end dates in a single state update
  const updateDateRange = (
    id: string,
    startDate: string,
    endDate: string
  ) => {
    const updatedRows = filterRows.map((row) => 
      row.id === id 
        ? { ...row, startDate, endDate }
        : row
    );
    setFilterRows(updatedRows);
    // Save to localStorage whenever form values change
    saveFormState(updatedRows, aggregated);
  };

  // Extract API call logic into a reusable function
  const fetchMetrics = async (useAggregated: boolean) => {
    setLoading(true);
    setError(null);
    try {
      // Prepare ranges array
      const ranges = filterRows
        .filter((row) => row.startDate) // Only include rows with start date
        .map((row) => {
          let parsedBlocks: number[] | undefined = undefined;
          
          if (row.startTime && row.endTime) {
            const startBlock = timeToBlock(row.startTime);
            const endBlock = timeToBlock(row.endTime);
            if (startBlock !== null && endBlock !== null && startBlock <= endBlock) {
              // If end time is 24:00, include the end block (block 96)
              // Otherwise, subtract 1 from endBlock so the range is exclusive of the end block
              // e.g., if start=1 and end=2, we only include block 1
              // If startBlock === endBlock, we still include that single block
              const isEndTime24 = row.endTime === "24:00";
              let effectiveEndBlock: number;
              if (isEndTime24) {
                // 24:00 means include block 96
                effectiveEndBlock = endBlock;
              } else {
                // For other end times, exclude the end block
                effectiveEndBlock = endBlock - 1;
              }
              // Ensure we have at least one block
              if (effectiveEndBlock >= startBlock) {
                const length = effectiveEndBlock - startBlock + 1;
                parsedBlocks = Array.from({ length }, (_, i) => startBlock + i);
              } else if (startBlock === endBlock) {
                // If start and end are the same (and not 24:00), include that single block
                parsedBlocks = [startBlock];
              }
            }
          }

          return {
            startDate: row.startDate,
            endDate: row.endDate || undefined,
            exchangeName: row.exchangeName || undefined,
            productName: row.productName || undefined,
            blocks: parsedBlocks,
          };
        });

      if (ranges.length === 0) {
        setError("At least one row with Start Date is required.");
        setLoading(false);
        return;
      }

      const response = await getCollectiveMetrics({
        ranges,
        aggregated: useAggregated,
      });
      setMetrics(response);
      setResultsAggregated(useAggregated); // Store the aggregated value used for these results
      // Save form state after successful API call
      saveFormState(filterRows, useAggregated);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch metrics.");
    }
    setLoading(false);
  };

  // Form submission handler.
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await fetchMetrics(aggregated);
  };

  // Auto-fetch results on mount if form has valid saved data
  useEffect(() => {
    // Check if we have saved state with valid data (at least one row with startDate)
    const hasValidData = savedState && savedState.filterRows.some((row) => row.startDate);
    
    if (hasValidData) {
      // Auto-fetch results with saved form data
      fetchMetrics(savedState.aggregated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Save aggregated state to localStorage when it changes (skip initial mount)
  useEffect(() => {
    if (isInitialAggregatedMount.current) {
      isInitialAggregatedMount.current = false;
      return;
    }
    saveFormState(filterRows, aggregated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregated]);

  // Auto-fetch when aggregated toggle changes (only if metrics already exist)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only auto-fetch if metrics already exist (user has submitted form at least once)
    if (metrics !== null) {
      fetchMetrics(aggregated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregated]);

  // Clear form handler
  const clearForm = () => {
    const defaultRows = [
      {
        id: Date.now().toString(),
        startDate: "",
        endDate: "",
        exchangeName: "IEX",
        productName: "DAM",
        startTime: "",
        endTime: "",
      },
    ];
    setFilterRows(defaultRows);
    setAggregated(true);
    setMetrics(null);
    setResultsAggregated(true); // Reset to default
    setError(null);
    // Clear localStorage
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing form state from localStorage:", error);
    }
  };

  // Utility function to format metric values to 3 decimal places if they are numbers.
  const formatMetricValue = (value: number | undefined): string => {
    return typeof value === "number" ? value.toFixed(3) : "N/A";
  };

  // Format range title: "Exchange Product - Start Date to End Date (start time to end time)"
  const formatRangeTitle = (row: FilterRow): string => {
    const exchange = row.exchangeName || "All";
    const product = row.productName || "All";
    const startDate = row.startDate || "";
    const endDate = row.endDate || row.startDate || "";
    
    let dateRange = startDate;
    if (endDate && endDate !== startDate) {
      dateRange = `${startDate} to ${endDate}`;
    }
    
    let timeRange = "";
    if (row.startTime && row.endTime) {
      timeRange = ` (${row.startTime} to ${row.endTime})`;
    } else if (row.startTime) {
      timeRange = ` (${row.startTime})`;
    }
    
    return `${exchange} ${product} - ${dateRange}${timeRange}`;
  };

  // Render metric card component
  const renderMetricCard = (metric: ExchangeMetrics, index: number) => (
    <>
      {/* Card 1: Weighted Avg Price */}
      <div className="flex flex-col items-center bg-gradient-to-br from-white to-blue-50 p-4 rounded-lg transform hover:scale-105 transition-all duration-300 flex-1 border border-blue-100">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mb-2">
          <IndianRupee className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
          Weighted Avg Price
        </h3>
        <p className="text-xl font-medium text-gray-800 mb-0.5">
          {formatMetricValue(metric.weighted_avg_price.value)}
        </p>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
          {metric.weighted_avg_price.unit}
        </span>
      </div>

      {/* Card 2: Avg Volume */}
      <div className="flex flex-col items-center bg-gradient-to-br from-white to-orange-50 p-4 rounded-lg transform hover:scale-105 transition-all duration-300 flex-1 border border-orange-100">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mb-2">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
          Avg Volume
        </h3>
        <p className="text-xl font-medium text-gray-800 mb-0.5">
          {formatMetricValue(metric.avg_volume.value)}
        </p>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
          {metric.avg_volume.unit}
        </span>
      </div>

      {/* Card 3: Total Volume */}
      <div className="flex flex-col items-center bg-gradient-to-br from-white to-teal-50 p-4 rounded-lg transform hover:scale-105 transition-all duration-300 flex-1 border border-teal-100">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mb-2">
          <Package className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">
          Total Volume
        </h3>
        <p className="text-xl font-medium text-gray-800 mb-0.5">
          {formatMetricValue(metric.total_volume_mu.value)}
        </p>
        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
          {metric.total_volume_mu.unit}
        </span>
      </div>
    </>
  );

  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl p-5 border border-gray-100"
      >
        {/* <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
          <h1 className="text-xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
            Collective Price Metrics
          </h1>
        </div> */}

        {/* Multiple Filter Rows */}
        <div className="space-y-3">
          {filterRows.map((row, index) => (
            <div
              key={row.id}
              className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200"
            >
              <div className="flex flex-row items-end gap-3 flex-wrap">
                {/* Date Range Picker */}
                <div className="flex-1 min-w-[250px]">
                  <label
                    className="block font-medium text-xs mb-1 text-gray-700"
                  >
                    Date Range <span className="text-red-500">*</span>
                  </label>
                  <div className="w-full">
                    <DynamicDatePicker
                      setDate={(dates) => {
                        if (dates && dates.length > 0) {
                          const startDate = dates[0].startDate;
                          const endDate = dates[0].endDate;
                          
                          const formatLocal = (d: Date) => {
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          };

                          const formattedStartDate = formatLocal(startDate);
                          const formattedEndDate = formatLocal(endDate);
                          // Update both dates in a single state update
                          updateDateRange(row.id, formattedStartDate, formattedEndDate);
                        }
                      }}
                      defaultDates={
                        row.startDate
                          ? {
                              startDate: new Date(row.startDate),
                              endDate: row.endDate ? new Date(row.endDate) : new Date(row.startDate),
                              key: "selection",
                            }
                          : undefined
                      }
                      buttonClassName="w-full px-2 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm cursor-pointer text-left"
                    />
                  </div>
                </div>

                {/* Exchange Name */}
                <div className="flex-1 min-w-[120px]">
                  <label
                    className="block font-medium text-xs mb-1 text-gray-700"
                  >
                    Exchange Name
                  </label>
                  <select
                    value={row.exchangeName}
                    onChange={(e) =>
                      updateFilterRow(row.id, "exchangeName", e.target.value)
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm cursor-pointer"
                  >
                    <option value="IEX">IEX</option>
                    <option value="PXIL">PXIL</option>
                    <option value="HPX">HPX</option>
                  </select>
                </div>

                {/* Product Name */}
                <div className="flex-1 min-w-[120px]">
                  <label
                    className="block font-medium text-xs mb-1 text-gray-700"
                  >
                    Product Name
                  </label>
                  <select
                    value={row.productName}
                    onChange={(e) =>
                      updateFilterRow(row.id, "productName", e.target.value)
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm cursor-pointer"
                  >
                    <option value="DAM">DAM</option>
                    <option value="RTM">RTM</option>
                    <option value="GDAM">GDAM</option>
                    <option value="HPDAM">HPDAM</option>
                  </select>
                </div>

                {/* Start Time */}
                <div className="flex-1 min-w-[120px]">
                  <label
                    className="block font-medium text-xs mb-1 text-gray-700"
                  >
                    Start Time
                  </label>
                  <select
                    value={row.startTime}
                    onChange={(e) =>
                      updateFilterRow(row.id, "startTime", e.target.value)
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm cursor-pointer"
                  >
                    <option value="">Select Time</option>
                    {generateTimeOptions().map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                {/* End Time */}
                <div className="flex-1 min-w-[120px]">
                  <label
                    className="block font-medium text-xs mb-1 text-gray-700"
                  >
                    End Time
                  </label>
                  <select
                    value={row.endTime}
                    onChange={(e) =>
                      updateFilterRow(row.id, "endTime", e.target.value)
                    }
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all duration-200 bg-white text-gray-700 text-sm cursor-pointer"
                  >
                    <option value="">Select Time</option>
                    {generateTimeOptions().map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Add/Remove Buttons */}
                <div className="flex flex-row gap-2 items-end">
                  <button
                    type="button"
                    onClick={() => addFilterRow(row.id)}
                    className="w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 text-white transition-all duration-200 transform hover:scale-110 flex items-center justify-center"
                    title="Add Row"
                  >
                    <Plus className="w-5 h-5" strokeWidth={3} />
                  </button>
                  {filterRows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFilterRow(row.id)}
                      className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all duration-200 transform hover:scale-110 flex items-center justify-center text-xl"
                      title="Remove Row"
                    >
                      <span>×</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Submit and Clear Buttons */}
        <div className="flex justify-center gap-3 mt-2 pt-3">
          <button
            type="button"
            className="px-6 py-2 rounded-lg text-gray-700 font-medium text-sm border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105"
            onClick={clearForm}
          >
            <i className="fas fa-redo mr-1.5 text-xs"></i>
            Clear
          </button>
          <button
            type="submit"
            className="px-6 py-2 rounded-lg text-white font-medium text-sm transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style={{
              background: loading
                ? "#ccc"
                : `linear-gradient(135deg, ${PrimaryColor}, #ff9a3c)`,
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1.5 text-xs"></i>
                Loading...
              </>
            ) : (
              <>
                <i className="fas fa-chart-line mr-1.5 text-xs"></i>
                Get Metrics
              </>
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="mt-3 bg-red-50 border-l-4 border-red-500 p-3 rounded-lg">
          <div className="flex items-center">
            <i className="fas fa-exclamation-circle text-red-500 mr-2 text-sm"></i>
            <p className="text-red-700 font-medium text-sm">{error}</p>
          </div>
        </div>
      )}

      {metrics && (
        <div className="mt-3 bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
            <h2 className="text-lg font-semibold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
              Results
            </h2>
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
              <label
                htmlFor="aggregated"
                className="font-medium text-xs cursor-pointer flex items-center gap-2"
                style={{ color: SecondaryColor }}
              >
                <div className="relative inline-block w-10 h-5">
                  <input
                    type="checkbox"
                    id="aggregated"
                    checked={aggregated}
                    onChange={(e) => setAggregated(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`block w-10 h-5 rounded-full transition-colors duration-200 ${
                      aggregated
                        ? "bg-gradient-to-r"
                        : "bg-gray-300"
                    }`}
                    style={
                      aggregated
                        ? {
                            background: `linear-gradient(to right, ${PrimaryColor}, #ff9a3c)`,
                          }
                        : {}
                    }
                  ></div>
                  <div
                    className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${
                      aggregated ? "transform translate-x-5" : ""
                    }`}
                  ></div>
                </div>
                <span>Aggregated</span>
              </label>
            </div>
          </div>
          
          {resultsAggregated ? (
            // Aggregated: Single result
            Array.isArray(metrics) && metrics.length > 0 ? (
              <div className="flex flex-row gap-3">
                {renderMetricCard(metrics[0], 0)}
              </div>
            ) : !Array.isArray(metrics) ? (
              <div className="flex flex-row gap-3">
                {renderMetricCard(metrics, 0)}
              </div>
            ) : null
          ) : (
            // Non-aggregated: Multiple results
            Array.isArray(metrics) ? (
              <div className="space-y-4">
                {metrics.map((metric, index) => {
                  // Get the corresponding filter row (only rows with startDate are sent)
                  const validRows = filterRows.filter((row) => row.startDate);
                  const correspondingRow = validRows[index];
                  const rangeTitle = correspondingRow 
                    ? formatRangeTitle(correspondingRow)
                    : `Range ${index + 1}`;
                  
                  return (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
                        <i className="fas fa-filter text-orange-500 text-xs"></i>
                        {rangeTitle}
                      </h3>
                      <div className="flex flex-row gap-3">
                        {renderMetricCard(metric, index)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-row gap-3">
                {renderMetricCard(metrics, 0)}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ExchangeMetricsForm;
